import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { Badge, Text } from "@mantine/core";
import type { DragEvent, ReactNode } from "react";
import { useState } from "react";

import { useFileManager, useFileManagerUI } from "@/components/core/FileManager";
import type { FileManagerItem, UploadFileItem } from "@/components/core/FileManager";
import { FileManagerItemContextMenu } from "@/components/core/FileManager";
import { FileManagerItemDndWrapper } from "@/components/core/FileManager";
import { useFileManagerDndActive } from "../FileManagerDndProvider/FileManagerDndProvider";
import { getIconConfig } from "../FileManagerItemCard/FileManagerItemCard";
import { FileManagerItemCard } from "../FileManagerItemCard/FileManagerItemCard";

import styles from "./FileManagerBody.module.scss";

function DragOverlayCard({ item, count }: { item: FileManagerItem; count: number }) {
  const { Icon, color } = getIconConfig(item);
  return (
    <div className={styles.dragOverlay}>
      <Icon size={24} color={color} />
      <span className={styles.dragOverlayName}>{item.name}</span>
      {count > 1 && (
        <Badge size="xs" circle>
          {count}
        </Badge>
      )}
    </div>
  );
}

interface FileManagerBodyProps {
  showCheckboxes?: boolean;
  renderItem?: (item: FileManagerItem, isOsDragOver: boolean) => ReactNode;
}

export function FileManagerBody({ showCheckboxes, renderItem }: FileManagerBodyProps) {
  const { items, openFolder, moveItems, upload } = useFileManager();
  const { selectedItems, isSelected, exitSelectionMode } = useFileManagerUI();
  const hasDndProvider = useFileManagerDndActive();

  const [activeItem, setActiveItem] = useState<FileManagerItem | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileDragHoverFolderId, setFileDragHoverFolderId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  function isFileDrag(event: DragEvent): boolean {
    return Array.from(event.dataTransfer.types).includes("Files");
  }

  function getHoveredFolderId(target: EventTarget | null): string | null {
    const folderEl = (target as HTMLElement | null)?.closest<HTMLElement>(
      "[data-item-type='FOLDER']",
    );
    return folderEl?.dataset.itemId ?? null;
  }

  function handleFileDragEnter(event: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    setIsDragOver(true);
    setFileDragHoverFolderId(getHoveredFolderId(event.target));
  }

  function handleFileDragOver(event: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(event)) return;
    event.preventDefault();
  }

  function handleFileDragLeave(event: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(event)) return;
    if ((event.currentTarget as HTMLDivElement).contains(event.relatedTarget as Node | null))
      return;
    setIsDragOver(false);
    setFileDragHoverFolderId(null);
  }

  function handleFileDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragOver(false);

    const droppedFiles = Array.from(event.dataTransfer.files);
    const targetFolderId = getHoveredFolderId(event.target);
    setFileDragHoverFolderId(null);

    if (droppedFiles.length === 0) return;

    const uploadItems: UploadFileItem[] = droppedFiles.map((file) => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
    }));

    upload(uploadItems, targetFolderId ?? undefined);
  }

  function handleDragStart({ active }: DragStartEvent) {
    const item = active.data.current?.item as FileManagerItem | undefined;
    setActiveItem(item ?? null);
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    setActiveItem(null);

    if (!over) return;

    const dragged = active.data.current?.item as FileManagerItem;
    const target = over.data.current?.item as FileManagerItem;

    if (!dragged || !target) return;
    if (target.type !== "FOLDER") return;
    if (target.id === dragged.id) return;
    if (target.parentId === dragged.id) return;
    if (target.id === dragged.parentId) return;

    const itemsToMove = isSelected(dragged.id) ? selectedItems : [dragged];
    const filtered = itemsToMove.filter((i) => i.id !== target.id);

    if (filtered.length === 0) return;

    moveItems(
      {
        items: filtered.map((i) => ({ id: i.id, name: i.name, type: i.type })),
        destination: { id: target.id, name: target.name },
      },
      { onSuccess: () => exitSelectionMode(), onError: () => {} },
    );
  }

  const dragCount = activeItem && isSelected(activeItem.id) ? selectedItems.length : 1;

  function defaultRenderItem(item: FileManagerItem): ReactNode {
    return (
      <FileManagerItemDndWrapper item={item} isOsDragOver={fileDragHoverFolderId === item.id}>
        <FileManagerItemContextMenu item={item}>
          <FileManagerItemCard
            item={item}
            showCheckboxes={showCheckboxes}
            onOpen={(i) => {
              if (i.type === "FOLDER") openFolder(i.id, i.name);
            }}
          />
        </FileManagerItemContextMenu>
      </FileManagerItemDndWrapper>
    );
  }

  const render = renderItem ?? defaultRenderItem;

  const bodyContent = (
    <div
      data-slot={"file-manager-body"}
      className={`${styles.fileManagerBody} ${isDragOver && !fileDragHoverFolderId ? styles.isDragOver : ""}`}
      onDragEnter={handleFileDragEnter}
      onDragOver={handleFileDragOver}
      onDragLeave={handleFileDragLeave}
      onDrop={handleFileDrop}
    >
      {isDragOver && !fileDragHoverFolderId && (
        <div className={styles.dropOverlay}>
          <Text fw={500} size="lg" c="blue">
            Drop files to upload
          </Text>
        </div>
      )}
      <div className={styles.grid}>
        {items.map((item) => (
          <div key={item.id} style={{ display: "contents" }}>
            {render(item, fileDragHoverFolderId === item.id)}
          </div>
        ))}
      </div>
    </div>
  );

  if (hasDndProvider) {
    return bodyContent;
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      {bodyContent}
      <DragOverlay dropAnimation={null}>
        {activeItem ? <DragOverlayCard item={activeItem} count={dragCount} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
